import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { GalerieService, PhotoGalerie } from 'src/app/core/services/entities/galerie.service';
import { LIENS_DOCUMENT } from 'src/app/core/models/document.model';

/** Type du lien (serveur) → route de la fiche */
const ROUTES_LIEN: Record<string, (id: number) => any[]> = {
  LOT_VOLAILLE: id => ['/volaille/details', id], BASSIN: id => ['/bassins/details', id],
  PISCICULTURE: id => ['/pisciculture/details', id], TROUPEAU: id => ['/betail/troupeau/details', id],
  ANIMAL: id => ['/betail/details', id], CULTURE: id => ['/maraichage/details', id],
  ARTICLE_STOCK: id => ['/stock', id], EQUIPEMENT: id => ['/equipements', id],
};

/**
 * Visionneuse plein écran de la Galerie : image d'affichage (jamais l'original), flèches, ←/→, balayage,
 * zoom (molette, double clic), Échap ; diaporama toutes les 4 s ; bandeau masquable (date, auteur, fiche, légende).
 */
@Component({
  selector: 'bay-visionneuse',
  template: `
    <div class="bv" role="dialog" aria-modal="true" [attr.aria-label]="'Photo ' + (index + 1) + ' sur ' + nombre" tabindex="-1" #racine
         (click)="arreterDiapo()" data-etat="visionneuse">
      <div class="bv-haut" (click)="$event.stopPropagation()">
        <span class="bv-compteur" aria-live="polite">Photo {{ index + 1 }} sur {{ nombre }}</span>
        <div class="d-flex gap-1">
          <button type="button" class="bv-btn" (click)="basculerDiapo()" [attr.aria-label]="diapo ? 'Mettre en pause le diaporama' : 'Lancer le diaporama'"
                  [attr.aria-pressed]="diapo" data-test="diaporama">
            <span class="material-symbols-outlined" aria-hidden="true">{{ diapo ? 'pause' : 'play_arrow' }}</span>
          </button>
          <button type="button" class="bv-btn" (click)="bandeau = !bandeau" [attr.aria-pressed]="bandeau" aria-label="Informations sur la photo">
            <span class="material-symbols-outlined" aria-hidden="true">info</span>
          </button>
          <button type="button" class="bv-btn" (click)="galerie.telechargerOriginal(photo)" aria-label="Télécharger l'original" title="Télécharger l'original">
            <span class="material-symbols-outlined" aria-hidden="true">download</span>
          </button>
          <button *ngIf="photo?.peutSupprimer" type="button" class="bv-btn" (click)="supprimer()" aria-label="Supprimer la photo" title="Supprimer">
            <span class="material-symbols-outlined" aria-hidden="true">delete</span>
          </button>
          <button type="button" class="bv-btn" (click)="fermer.emit()" aria-label="Fermer (Échap)">
            <span class="material-symbols-outlined" aria-hidden="true">close</span>
          </button>
        </div>
      </div>

      <div class="bv-scene" (wheel)="molette($event)" (dblclick)="doubleClic()" (pointerdown)="debut($event)"
           (pointermove)="deplacer($event)" (pointerup)="fin($event)" (pointercancel)="fin($event)">
        <img *ngIf="(src$ | async) as s; else attente" [src]="s" [alt]="photo?.legende || photo?.nom || 'Photo'" draggable="false"
             class="bv-img" [style.transform]="'translate(' + tx + 'px,' + ty + 'px) scale(' + zoom + ')'">
        <ng-template #attente><div class="spinner-border text-light" role="status"><span class="visually-hidden">Chargement…</span></div></ng-template>
      </div>

      <button *ngIf="index > 0" type="button" class="bv-nav bv-prec" (click)="$event.stopPropagation(); aller(-1)" aria-label="Photo précédente">
        <span class="material-symbols-outlined" aria-hidden="true">chevron_left</span>
      </button>
      <button *ngIf="index < nombre - 1" type="button" class="bv-nav bv-suiv" (click)="$event.stopPropagation(); aller(1)" aria-label="Photo suivante">
        <span class="material-symbols-outlined" aria-hidden="true">chevron_right</span>
      </button>

      <div *ngIf="bandeau && photo" class="bv-bandeau" (click)="$event.stopPropagation()" data-etat="bandeau">
        <div class="fw-semibold">{{ libelleDate }}</div>
        <div>{{ photo.auteur?.nom ? 'Prise par ' + photo.auteur?.nom : 'Auteur inconnu' }}</div>
        <a *ngIf="routeLien as r" href="javascript:void(0)" class="bv-lien" (click)="ouvrirFiche(r)">
          <span class="material-symbols-outlined align-middle" style="font-size:16px;" aria-hidden="true">{{ iconeLien }}</span>
          {{ photo.lien?.label || 'Voir la fiche' }}
        </a>
        <span *ngIf="!routeLien && photo.lien?.label">{{ photo.lien?.label }}</span>
        <div *ngIf="photo.legende" class="bv-legende">{{ photo.legende }}</div>
      </div>
    </div>
  `,
  styles: [`
    .bv { position:fixed; inset:0; z-index:2000; background:#000; color:#fff; display:flex; flex-direction:column; outline:none; }
    .bv-haut { display:flex; justify-content:space-between; align-items:center; padding:10px 12px; position:relative; z-index:2;
      background:linear-gradient(#000c, #0000); }
    .bv-compteur { font-size:14px; }
    .bv-btn { width:44px; height:44px; border:none; border-radius:50%; background:#ffffff1a; color:#fff;
      display:inline-flex; align-items:center; justify-content:center; }
    .bv-btn:hover, .bv-btn:focus-visible { background:#ffffff33; outline:2px solid #fff; }
    .bv-scene { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; overflow:hidden; touch-action:none; }
    .bv-img { max-width:100%; max-height:100%; object-fit:contain; user-select:none; animation:bv-fondu .25s ease; transition:transform .15s; }
    @keyframes bv-fondu { from { opacity:0; } to { opacity:1; } }
    @media (prefers-reduced-motion: reduce) { .bv-img { animation:none; transition:none; } }
    .bv-nav { position:absolute; top:50%; transform:translateY(-50%); z-index:2; width:52px; height:52px; border:none; border-radius:50%;
      background:#ffffff26; color:#fff; display:flex; align-items:center; justify-content:center; }
    .bv-nav .material-symbols-outlined { font-size:32px; }
    .bv-nav:focus-visible { outline:2px solid #fff; }
    .bv-prec { left:12px; } .bv-suiv { right:12px; }
    .bv-bandeau { position:absolute; left:0; right:0; bottom:0; z-index:2; padding:16px 20px 20px; font-size:14px;
      background:linear-gradient(#0000, #000d 35%); display:flex; flex-direction:column; gap:2px; }
    .bv-lien { color:#a3f69c; text-decoration:none; font-weight:600; }
    .bv-lien:hover { text-decoration:underline; }
    .bv-legende { margin-top:4px; color:#e5e7eb; }
  `]
})
export class VisionneuseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() photos: PhotoGalerie[] = [];
  @Input() index = 0;
  /** Nombre total (pages pas encore chargées comprises) */
  @Input() total?: number;
  @Output() fermer = new EventEmitter<void>();
  @Output() indexChange = new EventEmitter<number>();
  @Output() supprimee = new EventEmitter<number>();
  /** Fin de la liste chargée approchée : le parent charge la page suivante */
  @Output() plus = new EventEmitter<void>();
  @ViewChild('racine', { static: true }) racine!: ElementRef<HTMLElement>;

  src$: Observable<SafeUrl | null> = of(null);
  bandeau = true;
  diapo = false;
  zoom = 1; tx = 0; ty = 0;
  private minuterie?: any;
  private depart?: { x: number; y: number; tx: number; ty: number; t: number };
  private focusAvant: Element | null = null;

  constructor(public galerie: GalerieService, private sanitizer: DomSanitizer, private router: Router) {}

  get photo(): PhotoGalerie { return this.photos[this.index]; }
  get nombre(): number { return Math.max(this.total || 0, this.photos.length); }

  get libelleDate(): string {
    const p = this.photo;
    const d = p?.datePrise || p?.dateEnvoi;
    if (!d) { return 'Date inconnue'; }
    const txt = new Date(d).toLocaleString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    return (p.datePriseSource === 'ENVOI' ? 'Envoyée le ' : 'Prise le ') + txt;
  }

  get routeLien(): any[] | null {
    const l = this.photo?.lien;
    if (!l?.type || l.id == null) { return null; }
    const r = ROUTES_LIEN[l.type] || LIENS_DOCUMENT[l.type]?.route;
    return r ? r(l.id) : null;
  }

  get iconeLien(): string { return LIENS_DOCUMENT[this.photo?.lien?.type || '']?.icon || 'link'; }

  ngOnInit(): void {
    this.focusAvant = document.activeElement;
    document.body.style.overflow = 'hidden';
    setTimeout(() => this.racine.nativeElement.focus());
  }

  ngOnChanges(): void { this.afficher(); }

  ngOnDestroy(): void {
    this.arreterDiapo();
    document.body.style.overflow = '';
    (this.focusAvant as HTMLElement)?.focus?.();
  }

  private afficher(): void {
    const p = this.photo;
    this.zoom = 1; this.tx = 0; this.ty = 0;
    this.src$ = p ? this.galerie.affichage(p.id).pipe(map(u => u ? this.sanitizer.bypassSecurityTrustUrl(u) : null)) : of(null);
    // Voisines préchargées ; page suivante demandée à 5 photos de la fin
    for (const i of [this.index + 1, this.index - 1, this.index + 2]) { const v = this.photos[i]; if (v) { this.galerie.precharger(v.id); } }
    if (this.index >= this.photos.length - 5 && this.photos.length < this.nombre) { this.plus.emit(); }
  }

  aller(pas: number): void {
    const i = this.index + pas;
    if (i < 0 || i >= this.photos.length) { return; }
    this.index = i;
    this.indexChange.emit(i);
    this.afficher();
  }

  basculerDiapo(): void {
    if (this.diapo) { this.arreterDiapo(); return; }
    this.diapo = true;
    this.minuterie = setInterval(() => {
      if (this.index < this.photos.length - 1) { this.aller(1); } else { this.arreterDiapo(); }
    }, 4000);
  }

  arreterDiapo(): void {
    this.diapo = false;
    if (this.minuterie) { clearInterval(this.minuterie); this.minuterie = undefined; }
  }

  @HostListener('document:keydown', ['$event'])
  touche(e: KeyboardEvent): void {
    if (Swal.isVisible()) { return; }
    if (e.key === 'Escape') { e.preventDefault(); this.fermer.emit(); return; }
    if (this.diapo && e.key !== ' ') { this.arreterDiapo(); }
    if (e.key === 'ArrowRight') { e.preventDefault(); this.aller(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); this.aller(-1); }
    else if (e.key === ' ' && (e.target as HTMLElement)?.tagName !== 'BUTTON') { e.preventDefault(); this.basculerDiapo(); }
  }

  molette(e: WheelEvent): void {
    e.preventDefault();
    this.zoom = Math.min(4, Math.max(1, this.zoom * (e.deltaY < 0 ? 1.2 : 1 / 1.2)));
    if (this.zoom === 1) { this.tx = 0; this.ty = 0; }
  }

  doubleClic(): void {
    this.zoom = this.zoom > 1 ? 1 : 2.5;
    this.tx = 0; this.ty = 0;
  }

  debut(e: PointerEvent): void {
    this.depart = { x: e.clientX, y: e.clientY, tx: this.tx, ty: this.ty, t: Date.now() };
  }

  deplacer(e: PointerEvent): void {
    if (!this.depart || this.zoom === 1) { return; }
    this.tx = this.depart.tx + (e.clientX - this.depart.x);
    this.ty = this.depart.ty + (e.clientY - this.depart.y);
  }

  /** Balayage horizontal (sans zoom) : photo suivante ou précédente */
  fin(e: PointerEvent): void {
    const d = this.depart;
    this.depart = undefined;
    if (!d || this.zoom > 1) { return; }
    const dx = e.clientX - d.x, dy = e.clientY - d.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && Date.now() - d.t < 800) {
      this.arreterDiapo();
      this.aller(dx < 0 ? 1 : -1);
    }
  }

  ouvrirFiche(r: any[]): void { this.fermer.emit(); this.router.navigate(r); }

  supprimer(): void {
    const p = this.photo;
    this.arreterDiapo();
    Swal.fire({
      title: 'Supprimer cette photo ?', text: 'Elle part à la corbeille des documents et disparaît de la Galerie et des fiches.',
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#dc2626', confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
      // Au-dessus de la visionneuse (z-index 2000)
      didOpen: () => { const c = Swal.getContainer(); if (c) { c.style.zIndex = '2100'; } }
    }).then(r => {
      if (!r.isConfirmed) { return; }
      this.galerie.supprimer(p.id).subscribe({
        next: () => this.supprimee.emit(p.id),
        error: err => Swal.fire({ icon: 'error', title: 'Suppression impossible', text: err?.error?.message || 'Réessayez plus tard.' })
      });
    });
  }
}
